import React, { useCallback } from 'react'
import classNames from 'classnames'
import { Case, Switch } from 'react-if'
import { GuildScore } from '@/atoms/GuildScore'
import { Image } from '@/atoms/Image'
import { CaptionLG, CaptionMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver'
import {
  BrandLandingTabItemInteraction,
  useIsolatedCardInteractions,
  useImpressionTracking,
} from '@/services/Analytics/ContentInteractions'
import { Project } from '@/services/ProjectsService'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

export interface FranchiseProjectCardProps {
  relatedProject: Project
  itemIndex: number
  pagePlacement: number
  className?: string
  onProjectClick?: (project: Project) => void
}

export const FranchiseProjectCard: React.FC<FranchiseProjectCardProps> = ({
  relatedProject,
  itemIndex,
  pagePlacement,
  className,
  onProjectClick,
}) => {
  const { t } = useTranslate('watch')
  const [cardRef, isVisible] = useIntersectionObserver<HTMLDivElement>({
    threshold: 0.9,
  })

  const projectId = relatedProject?.title?.id || relatedProject?.id || ''
  const portraitProjectPoster =
    relatedProject?.title?.portraitAngelImage?.cloudinaryPath ??
    relatedProject?.title?.portraitAngelImage3?.cloudinaryPath ??
    relatedProject?.title?.portraitTitleImage?.cloudinaryPath ??
    ''

  const createInteraction = useCallback(
    (item: Project) => {
      const id = item?.title?.id || item?.id || ''
      const typename = (item?.title?.__typename as 'ContentSeries' | 'ContentMovie' | 'ContentSpecial' | null) || null
      const thumbnailPath =
        item?.title?.portraitAngelImage?.cloudinaryPath ??
        item?.title?.portraitAngelImage3?.cloudinaryPath ??
        item?.title?.portraitTitleImage?.cloudinaryPath

      if (!id || !thumbnailPath) return null

      return new BrandLandingTabItemInteraction({
        id,
        __typename: typename,
        attribution: {
          itemIndex,
          pagePlacement,
          image: {
            cloudinaryPath: thumbnailPath,
          },
        },
      })
        .withSurface('brand_landing')
        .withPlacement('franchise_tab', pagePlacement, itemIndex, 'horizontal_swimlane')
        .withImageArtwork(thumbnailPath, 'portrait_display')
    },
    [itemIndex, pagePlacement],
  )

  const { handleMouseEnter, handleMouseLeave, handleClick } = useIsolatedCardInteractions({
    contentId: projectId,
    item: relatedProject,
    itemIndex,
    createInteraction,
    onClickTrack: () => onProjectClick?.(relatedProject),
  })

  useImpressionTracking({
    isVisible,
    contentId: projectId,
    item: relatedProject,
    itemIndex,
    createInteraction,
  })

  return (
    <div
      ref={cardRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      aria-label="suggested-content-card"
      id={relatedProject?.id ?? ''}
      className="relative min-w-[250px] pr-6 lg:min-w-[316px]"
    >
      <div className={classNames('flex flex-col', className)}>
        {portraitProjectPoster && (
          <a
            aria-label={relatedProject?.name ?? ''}
            href={`${paths.watch.index}/${relatedProject?.slug}`}
            className="aspect-h-3 aspect-w-2 relative mb-3 w-full cursor-pointer overflow-hidden rounded-2xl"
            onClick={handleClick}
          >
            <div className="absolute inset-0">
              <Image
                alt={relatedProject?.title?.name ?? ''}
                src={getCloudinaryImageUrl({ path: portraitProjectPoster })}
                width={316}
                height={474}
                sizes="(max-width: 1024px) 250px, 316px"
                className="h-full w-full object-cover"
              />
            </div>
          </a>
        )}
        <div className="flex flex-col gap-y-1">
          <CaptionMD color="core-gray-500">
            <Switch>
              <Case condition={relatedProject?.title?.__typename === 'ContentSeries'}>
                <Translate i18nKey="series" t={t}>
                  Series
                </Translate>
              </Case>
              <Case condition={relatedProject?.title?.__typename === 'ContentMovie'}>
                <Translate i18nKey="movie" t={t}>
                  Movie
                </Translate>
              </Case>
              <Case condition={relatedProject?.title?.__typename === 'ContentSpecial'}>
                <Translate i18nKey="special" t={t}>
                  Special
                </Translate>
              </Case>
            </Switch>
          </CaptionMD>
          <CaptionLG weight="bold">{relatedProject?.name}</CaptionLG>
          {relatedProject?.highestScore ? <GuildScore guildScore={relatedProject?.highestScore} /> : null}
        </div>
      </div>
    </div>
  )
}
